import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/utils/api";

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  return (
    <div
      className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2" aria-label="Schliessen" data-testid="lightbox-close">
        <X className="w-7 h-7" />
      </button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-3 sm:left-6 text-white/70 hover:text-white z-10 p-2 bg-black/30 rounded-full" aria-label="Vorheriges Bild" data-testid="lightbox-prev">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-3 sm:right-6 text-white/70 hover:text-white z-10 p-2 bg-black/30 rounded-full" aria-label="Naechstes Bild" data-testid="lightbox-next">
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}
      <img
        src={images[index].image_url}
        alt={images[index].alt_text}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">{index + 1} / {images.length}</div>
    </div>
  );
};

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api.get('/gallery')
      .then((res) => setImages(res.data || []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, []);

  const openLightbox = useCallback((idx) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1)), [images.length]);
  const nextImage = useCallback(() => setLightboxIndex((i) => (i === images.length - 1 ? 0 : i + 1)), [images.length]);

  // Hide section if no images and not loading
  if (!loading && images.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-[#faf8f5]" data-testid="gallery-section" id="projekte">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#4A331D] mb-3" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Echte Projekte. Echte Ergebnisse.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Hier sehen Sie echte Baustellen und fertige Arbeiten unserer Kundenprojekte.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {images.map((img, idx) => (
            <button
              key={img.id || idx}
              type="button"
              onClick={() => openLightbox(idx)}
              className="group relative overflow-hidden rounded-[10px] border border-[#e8dfd4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AA6A]"
              data-testid={`gallery-item-${idx}`}
              data-track="gallery_open"
              data-track-value={String(idx)}
              aria-label={`Bild vergroessern: ${img.alt_text}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={img.image_url}
                  alt={img.alt_text}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                />
              </div>
              {img.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-white text-sm font-medium text-left">{img.title}</div>
                  {img.category && <div className="text-white/70 text-xs text-left">{img.category}</div>}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 rounded-[10px]" />
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
};

export default Gallery;
